import React, { useState, useEffect, useCallback } from "react";
import blogService from "../../api/services/blogService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import Loading from "../../components/Loading";
import Modal from "../../components/common/Modal";
import SectionCard from "../../components/common/SectionCard";
import BlogForm from "../../components/blog/BlogForm";
import BlogTable from "../../components/blog/BlogTable";
import CategoryList from "../../components/category/CategoryList";
import CategoryService from "../../api/services/categoryService";

const initialBlogs = [];

const Blogs = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("blogs");
  const [categories, setCategories] = useState([]);
  const [blogs, setBlogs] = useState(initialBlogs);
  const [isLoading, setIsLoading] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingcategory, setEditingcategory] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [blogModalOpen, setBlogModalOpen] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [blogForm, setBlogForm] = useState({
    name: "",
    metaTitle: "",
    metaDescription: "",
    category: "",
    descriptionHtml: "",
    imageFile: null,
    imagePreview: "",
    imageScale: 100,
    file: "",
  });
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 15;
  console.log("blogFormblogFormblogFormblogForm", blogForm);
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    type: null,
    id: null,
  });

  const getAllCategory = async () => {
    const response = await CategoryService.getAllCategory();
    setCategories(response?.category || []);
  };

  useEffect(() => {
    getAllCategory();
  }, []);

  const resetBlogForm = () => {
    setEditingBlogId(null);
    setBlogForm({
      name: "",
      metaTitle: "",
      metaDescription: "",
      category: categories[0]?.id || "",
      descriptionHtml: "",
      imageFile: null,
      imagePreview: "",
      imageScale: 100,
      file: "",
    });
  };

  const openCreateBlog = () => {
    resetBlogForm();
    setBlogModalOpen(true);
  };

  const openEditBlog = (blog) => {
    setEditingBlogId(blog.id || blog._id);
    const resolvedCategory =
      blog.categoryId ||
      blog.category_id ||
      (blog.category &&
        typeof blog.category === "object" &&
        blog.category.id) ||
      (typeof blog.category === "string"
        ? categories.find((c) => c.name === blog.category)?.id || ""
        : "") ||
      "";
    setBlogForm({
      name: blog.title || blog.name || "",
      metaTitle: blog.metaTitle || blog.meta_title || "",
      metaDescription: blog.metaDescription || blog.meta_description || "",
      category: resolvedCategory,
      descriptionHtml:
        blog.description || blog.descriptionHtml || blog.content || "",
      imageFile: null,
      imagePreview: blog.image || blog.imageUrl || blog.coverImage || "",
      imageScale: blog.imageScale ?? 100,
      file: blog.image || "",
    });
    setBlogModalOpen(true);
  };

  const fetchBlogs = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await blogService.getAllBlogs(currentPage, limit);
      const list = Array.isArray(data) ? data : data?.blogs || [];
      setBlogs(list);
      setTotal(data?.total || list.length);
      setTotalPages(
        data?.totalPages || Math.ceil((data?.total || list.length) / limit)
      );
    } catch (err) {
      if (err?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
      toast.error(err?.message || "Failed to load blogs");
    } finally {
      setIsLoading(false);
    }
  }, [navigate, currentPage, limit]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const saveBlog = async () => {
    if (!blogForm.name.trim()) return toast.error("Please enter a blog name");
    if (!blogForm.category) return toast.error("Please choose a category");

    const payload = {
      title: blogForm.name.trim(),
      metaTitle: (blogForm.metaTitle || "").trim(),
      metaDescription: (blogForm.metaDescription || "").trim(),
      category: blogForm.category,
      description: blogForm.descriptionHtml,
      file: blogForm.imageFile,
    };

    console.log("payload", payload);
    console.log("1234567", blogForm.imageFile);

    try {
      const action = editingBlogId
        ? blogService.updateBlog(editingBlogId, payload)
        : blogService.createBlog(payload);
      await toast.promise(action, {
        loading: editingBlogId ? "Saving blog..." : "Creating blog...",
        success: editingBlogId ? "Blog updated" : "Blog created",
        error: (e) => e?.message || "Operation failed",
      });
      setBlogModalOpen(false);
      resetBlogForm();
      setCurrentPage(1); // Reset to first page after create/update
      await fetchBlogs();
    } catch (err) {
      if (err?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
    }
  };

  const requestDeleteBlog = (id) =>
    setConfirmDelete({ open: true, type: "blog", id });

  const performDelete = async (id) => {
    if (!confirmDelete.open) return;
    if (confirmDelete.type === "blog") {
      try {
        await toast.promise(blogService.deleteBlog(confirmDelete.id), {
          loading: "Deleting blog...",
          success: "Blog deleted",
          error: (e) => e?.message || "Delete failed",
        });
        await fetchBlogs();
      } catch (err) {
        if (err?.status === 401) {
          toast.error("Session expired. Please log in again.");
          navigate("/login");
          return;
        }
      }
    }
    if (confirmDelete.type === "category") {
      try {
        await CategoryService.deleteCategory(id);
        toast.success("Category deleted");
        getAllCategory();
      } catch (error) {
        toast.error(error?.message || "Failed to delete category");
        return;
      }
    }
    setConfirmDelete({ open: false, type: null, id: null });
  };

  const openCreateCategory = async () => {
    setEditingcategory(null);
    setCategoryName("");
    setCategoryModalOpen(true);
  };

  const openEditCategory = (category, id) => {
    setEditingcategory(id);
    setCategoryName(category);
    setCategoryModalOpen(true);
  };

  const saveCategory = async () => {
    const name = categoryName.trim();
    if (!name) {
      toast.error("Please enter a category name");
      return;
    }
    if (editingcategory && categoryName) {
      const response = await CategoryService.updateCategory(editingcategory, {
        title: categoryName,
      });
      if (response?.responseCode === 200) {
        toast.success(response?.message || "Category updated");
        getAllCategory();
      } else {
        toast.error(response?.message || "Failed to update category");
      }
    } else {
      try {
        const response = await CategoryService.createCategory({ title: name });
        if (response?.responseCode === 201) {
          toast.success(response?.message || "Category created");
          getAllCategory();
        } else {
          toast.error(response?.message || "Failed to create category");
        }
      } catch (error) {
        toast.error(error?.message || "Failed to create category");
      }
    }
    setCategoryModalOpen(false);
  };

  const requestDeleteCategory = async (id) => {
    setConfirmDelete({ open: true, type: "category", id });
  };

  return (
    <div className="w-full p-4">
      <div className="mb-4 flex gap-2">
        <button
          type="button"
          onClick={() => setActiveTab("blogs")}
          className={`cursor-pointer rounded-lg border px-3 py-2 text-xs md:text-sm font-medium transition-colors ${
            activeTab === "blogs"
              ? "border-red-500 bg-red-500 text-white shadow-md"
              : "border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
          }`}>
          Blogs
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("categories")}
          className={`cursor-pointer rounded-lg border px-3 py-2 text-xs md:text-sm font-medium transition-colors ${
            activeTab === "categories"
              ? "border-red-500 bg-red-500 text-white shadow-md"
              : "border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
          }`}>
          Categories
        </button>
      </div>

      {activeTab === "blogs" ? (
        <SectionCard
          title="Blogs"
          subtitle="Create and manage blog posts"
          action={
            <button
              type="button"
              onClick={openCreateBlog}
              className="cursor-pointer text-xs md:text-sm rounded-lg border border-red-500 bg-red-500 px-4 py-2 text-white font-semibold hover:bg-red-600 transition-colors shadow-md">
              Create Blog
            </button>
          }>
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="p-6">
                <Loading />
              </div>
            ) : null}
            <BlogTable
              blogs={blogs}
              isLoading={isLoading}
              onEdit={openEditBlog}
              onDelete={requestDeleteBlog}
            />
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-xs md:text-sm font-medium text-gray-700">
                Showing page{" "}
                <span className="font-bold text-gray-900">{currentPage}</span>{" "}
                of <span className="font-bold text-gray-900">{totalPages}</span>{" "}
                (Total: <span className="font-bold text-red-600">{total}</span>{" "}
                blogs)
              </div>
              <div className="flex gap-2 items-center justify-center">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className={`px-4 py-2 border rounded-lg font-medium text-xs md:text-sm transition-colors ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                  }`}>
                  Previous
                </button>
                <span className="text-xs md:text-sm text-gray-700">
                  <span className="font-bold">
                    {(currentPage - 1) * limit + 1}
                  </span>{" "}
                  -{" "}
                  <span className="font-bold">
                    {Math.min(currentPage * limit, total)}
                  </span>{" "}
                  of <span className="font-bold text-red-600">{total}</span>
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 border rounded-lg font-medium text-xs md:text-sm transition-colors ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                  }`}>
                  Next
                </button>
              </div>
            </div>
          )}
        </SectionCard>
      ) : (
        <SectionCard
          title="Categories"
          subtitle="Create and manage categories"
          action={
            <button
              type="button"
              onClick={openCreateCategory}
              className="cursor-pointer rounded-lg text-xs md:text-sm border border-red-500 bg-red-500 px-4 py-2 text-white font-semibold hover:bg-red-600 transition-colors shadow-md">
              New Category
            </button>
          }>
          <CategoryList
            categories={categories}
            onEdit={openEditCategory}
            onDelete={requestDeleteCategory}
          />
        </SectionCard>
      )}

      <Modal
        open={blogModalOpen}
        title={editingBlogId ? "Edit Blog" : "Create Blog"}
        onClose={() => {
          setBlogModalOpen(false);
          resetBlogForm();
        }}
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setBlogModalOpen(false);
                resetBlogForm();
              }}
              className="cursor-pointer rounded-lg text-xs md:text-sm border border-gray-300 bg-white px-3 py-2 text-gray-900">
              Cancel
            </button>
            <button
              type="button"
              onClick={saveBlog}
              className="cursor-pointer rounded-lg text-xs md:text-sm border border-red-500 bg-red-500 px-4 py-2 text-white font-semibold hover:bg-red-600 transition-colors shadow-md">
              {editingBlogId ? "Save Changes" : "Create Blog"}
            </button>
          </>
        }>
        <BlogForm
          blogForm={blogForm}
          setBlogForm={setBlogForm}
          categories={categories}
          openCreateCategory={openCreateCategory}
        />
      </Modal>

      <Modal
        open={categoryModalOpen}
        title={editingcategory ? "Edit Category" : "Create Category"}
        onClose={() => {
          setCategoryModalOpen(false);
          setEditingcategory(null);
          setCategoryName("");
        }}
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setCategoryModalOpen(false);
                setEditingcategory(null);
                setCategoryName("");
              }}
              className="cursor-pointer rounded-lg border text-xs md:text-sm border-gray-300 bg-white px-3 py-2 text-gray-900">
              Cancel
            </button>
            <button
              type="button"
              onClick={saveCategory}
              className="cursor-pointer text-xs md:text-sm rounded-lg border border-red-500 bg-red-500 px-4 py-2 text-white font-semibold hover:bg-red-600 transition-colors shadow-md">
              {editingcategory ? "Save Changes" : "Create Category"}
            </button>
          </>
        }>
        <div className="grid gap-3">
          <div>
            <label className="mb-1 block text-xs md:text-sm md:text-lg font-semibold">
              Category Name
            </label>
            <input
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="Enter category name"
              className="w-full rounded-lg text-xs md:text-sm border border-gray-300 px-3 py-2"
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmDelete.open}
        title="Confirm Delete"
        onClose={() => setConfirmDelete({ open: false, type: null, id: null })}
        footer={
          <>
            <button
              type="button"
              onClick={() =>
                setConfirmDelete({ open: false, type: null, id: null })
              }
              className="cursor-pointer rounded-lg text-xs md:text-sm border border-gray-300 bg-white px-3 py-2 text-gray-900">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => performDelete(confirmDelete.id)}
              className="cursor-pointer rounded-lg text-xs md:text-sm border border-red-500 bg-red-500 px-4 py-2 text-white font-semibold hover:bg-red-600 transition-colors shadow-md">
              Delete
            </button>
          </>
        }>
        <div className="text-gray-700">
          Are you sure you want to delete this {confirmDelete.type}? This action
          cannot be undone.
        </div>
      </Modal>
    </div>
  );
};

export default Blogs;
